import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import Logo from "../Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { LayoutGrid, Users, CalendarClock, Receipt, LogOut, ChevronDown } from "lucide-react";

const NAV_ITEMS = [
  { to: "/employers/hub", label: "Overview", icon: LayoutGrid, end: true, testid: "hub-nav-overview" },
  { to: "/employers/hub/candidates", label: "Candidates", icon: Users, testid: "hub-nav-candidates" },
  { to: "/employers/hub/interviews", label: "Interviews", icon: CalendarClock, testid: "hub-nav-interviews" },
  { to: "/employers/hub/billing", label: "Billing", icon: Receipt, testid: "hub-nav-billing" },
];

/**
 * Employer Hub shell — calm, restrained private-workspace chrome.
 * Desktop: single top bar with inline nav. Mobile: top bar + compact
 * secondary nav row (four items, icon + label).
 */
export const EmployerHubLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [verification, setVerification] = useState(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    api
      .get("/api/auth/verification/status")
      .then((res) => setVerification(res.data))
      .catch(() => {});
  }, []);

  const resendVerification = async () => {
    setResending(true);
    try {
      await api.post("/api/auth/verification/request");
      toast.success("Verification email sent — check your inbox");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to send right now");
    } finally {
      setResending(false);
    }
  };

  const handleSignOut = () => {
    logout();
    navigate("/");
  };

  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase();

  const navLinkClass = ({ isActive }) =>
    `inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive
        ? "text-blue-700 bg-blue-50"
        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
    }`;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="employer-hub-layout">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/employers/hub" data-testid="hub-logo-link" className="flex items-center">
                <Logo height={30} />
              </Link>
              <nav className="hidden md:flex items-center gap-1" data-testid="hub-desktop-nav">
                {NAV_ITEMS.map(({ to, label, icon: Icon, end, testid }) => (
                  <NavLink key={to} to={to} end={end} className={navLinkClass} data-testid={testid}>
                    <Icon className="h-4 w-4" />
                    {label}
                  </NavLink>
                ))}
              </nav>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2.5 rounded-full pl-1 pr-2.5 py-1 hover:bg-gray-50 transition-colors"
                  data-testid="hub-account-menu-trigger"
                >
                  <span className="h-8 w-8 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">
                    {initials || "?"}
                  </span>
                  <span className="hidden sm:block text-left">
                    <span className="block text-sm font-medium text-gray-900 leading-tight">
                      {user?.first_name} {user?.last_name}
                    </span>
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="hub-account-menu">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium text-gray-900">
                    {user?.first_name} {user?.last_name}
                  </div>
                  <div className="text-xs text-gray-500 font-normal truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="text-gray-700 cursor-pointer"
                  data-testid="hub-sign-out"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile nav row */}
        <nav
          className="md:hidden border-t border-gray-100 bg-white px-2 py-1.5 flex items-center justify-around"
          data-testid="hub-mobile-nav"
        >
          {NAV_ITEMS.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-md text-[11px] font-medium ${
                  isActive ? "text-blue-700" : "text-gray-500"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      {verification && verification.email_verified === false && (
        <div className="bg-amber-50 border-b border-amber-200" data-testid="hub-verify-banner">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-sm text-amber-800">
              Please verify your email address — we sent a confirmation link to{" "}
              <span className="font-medium">{verification.email}</span>.
            </p>
            <button
              onClick={resendVerification}
              disabled={resending}
              className="self-start sm:self-auto text-sm font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700 disabled:opacity-50"
              data-testid="hub-verify-resend"
            >
              {resending ? "Sending…" : "Resend email"}
            </button>
          </div>
        </div>
      )}

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">{children}</div>
      </main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-gray-400">
          Inter-View Employer Hub · Your private service workspace
        </div>
      </footer>
    </div>
  );
};
